import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, ArrowLeft, Users, Rocket, CheckCircle2, Mail, Download, Copy, RefreshCw, IndianRupee,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api, API } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { PageTransition } from "@/components/PageTransition";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const FUNNEL_COLORS = ["#0055FF", "#0055FF", "#7C3AED", "#10B981", "#10B981", "#DB2777"];
const Q_LABELS = {
  q1: { yes: "Yes", no: "No", not_sure: "Not sure" },
  q2: { youtube: "YouTube", course: "Paid course/app", not_trying: "Not trying", other: "Other" },
  q4: { yes: "Has paid before", no: "Never paid" },
  q5: { love_it: "😍 Love it", useful: "🙂 Useful", meh: "😐 Meh", confusing: "😕 Confusing", not_useful: "🙁 Not useful" },
  q6: { yes: "Yes", maybe: "Maybe", no: "No" },
};

function Tile({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
      <span className={`grid place-items-center h-9 w-9 rounded-xl ${color}`}><Icon className="h-4.5 w-4.5" /></span>
      <p className="mt-3 font-heading font-extrabold tracking-tight text-3xl leading-none">{value}</p>
      <p className="mt-1.5 text-xs font-medium text-muted-foreground">{label}</p>
    </div>
  );
}

function Bars({ title, data, total }) {
  const t = total || Object.values(data).reduce((a, b) => a + b, 0);
  const entries = Object.entries(data);
  return (
    <div>
      <h3 className="font-heading font-bold text-base mb-3">{title}</h3>
      <div className="space-y-2.5">
        {entries.map(([k, v]) => {
          const pct = t ? Math.round((v / t) * 100) : 0;
          return (
            <div key={k}>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium">{k}</span>
                <span className="text-muted-foreground font-mono text-xs">{v} · {pct}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-secondary overflow-hidden">
                <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
        {!t && <p className="text-sm text-muted-foreground">No answers yet.</p>}
      </div>
    </div>
  );
}

function labelize(field, data) {
  const map = Q_LABELS[field] || {};
  const out = {};
  Object.entries(data).forEach(([k, v]) => { out[map[k] || k] = v; });
  return out;
}

function PriceCard({ label, stats }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <p className="text-sm font-semibold text-foreground">{label}</p>
      {stats.count ? (
        <>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            {[["Min", stats.min], ["Median", stats.median], ["Max", stats.max]].map(([l, v]) => (
              <div key={l} className="rounded-xl bg-secondary py-2">
                <p className="font-heading font-extrabold text-lg leading-none">₹{Math.round(v)}</p>
                <p className="text-[10px] font-medium text-muted-foreground mt-1 uppercase tracking-wider">{l}</p>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{stats.count} answers</p>
        </>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">No answers yet.</p>
      )}
    </div>
  );
}

export default function SurveyResults() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const load = () => {
    setErr(false);
    api.get("/pmf/admin/summary").then(({ data }) => setData(data)).catch(() => setErr(true));
  };
  useEffect(() => { window.scrollTo(0, 0); load(); }, []);

  const copyLink = () => {
    const url = `${window.location.origin}/survey`;
    navigator.clipboard?.writeText(url).then(() => toast.success("Survey link copied!")).catch(() => toast.message(url));
  };

  if (err) {
    return (
      <div className="min-h-screen bg-background"><Navbar />
        <PageTransition className="max-w-6xl mx-auto px-4 pt-24 pb-20">
          <div className="rounded-2xl border border-border bg-card p-10 text-center" data-testid="results-error">
            <p className="font-heading font-bold text-lg">Couldn't load survey results.</p>
            <Button onClick={load} className="mt-4 bg-primary text-primary-foreground">Try again</Button>
          </div>
        </PageTransition>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  const t = data.tiles;
  return (
    <div className="min-h-screen bg-background" data-testid="survey-results">
      <Navbar />
      <PageTransition className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-24">
        <button onClick={() => navigate("/admin")} data-testid="results-back"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to admin
        </button>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <span className="text-primary font-semibold tracking-[0.2em] text-xs uppercase">PMF Survey</span>
            <h1 className="mt-2 font-heading font-extrabold tracking-tight text-3xl md:text-4xl">Results & funnel</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" data-testid="copy-survey-link" onClick={copyLink}><Copy className="h-4 w-4" /> Copy survey link</Button>
            <Button variant="outline" size="sm" data-testid="refresh-results" onClick={load}><RefreshCw className="h-4 w-4" /> Refresh</Button>
            <a href={`${API}/pmf/admin/export/responses.csv`} data-testid="export-responses">
              <Button variant="outline" size="sm"><Download className="h-4 w-4" /> Responses CSV</Button>
            </a>
            <a href={`${API}/pmf/admin/export/events.csv`} data-testid="export-events">
              <Button variant="outline" size="sm"><Download className="h-4 w-4" /> Events CSV</Button>
            </a>
          </div>
        </div>

        {/* tiles */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-6 gap-4" data-testid="results-tiles">
          <Tile icon={Users} label="Visitors" value={t.visitors} color="bg-primary/10 text-primary" testid="tile-visitors" />
          <Tile icon={Rocket} label="Tried the app" value={t.tried} color="bg-indigo-500/10 text-indigo-500" testid="tile-tried" />
          <Tile icon={CheckCircle2} label="Completed" value={t.completed} color="bg-emerald-500/10 text-emerald-500" testid="tile-completed" />
          <Tile icon={Mail} label="Emails" value={t.emails} color="bg-pink-500/10 text-pink-500" testid="tile-emails" />
          <Tile icon={CheckCircle2} label="Completion rate" value={`${t.completion_rate}%`} color="bg-emerald-500/10 text-emerald-500" testid="tile-completion-rate" />
          <Tile icon={Rocket} label="Tryout rate" value={`${t.tryout_rate}%`} color="bg-indigo-500/10 text-indigo-500" testid="tile-tryout-rate" />
        </div>

        {/* funnel */}
        <div className="mt-6 rounded-2xl border border-border bg-card p-6 md:p-7" data-testid="results-funnel">
          <h2 className="font-heading font-extrabold tracking-tight text-xl">Funnel</h2>
          <p className="text-sm text-muted-foreground mt-1">How people moved through — with conversion % and drop-off at each step.</p>
          <div className="mt-5 space-y-3">
            {data.funnel.map((step, i) => {
              const prev = i > 0 ? data.funnel[i - 1].count : null;
              const drop = prev != null ? prev - step.count : null;
              const widthPct = data.funnel[0].count ? Math.max(4, Math.round((step.count / data.funnel[0].count) * 100)) : 4;
              return (
                <div key={step.key} data-testid={`funnel-${step.key}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-semibold">{step.label}</span>
                    <span className="text-muted-foreground">
                      <b className="text-foreground">{step.count}</b> · {step.pct}%
                      {drop != null && drop > 0 && <span className="text-destructive ml-2">−{drop} dropped</span>}
                    </span>
                  </div>
                  <div className="h-7 rounded-lg bg-secondary overflow-hidden">
                    <div className="h-full rounded-lg flex items-center px-3 text-xs font-bold text-white transition-all"
                      style={{ width: `${widthPct}%`, background: FUNNEL_COLORS[i % FUNNEL_COLORS.length] }}>
                      {step.count}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* exploration */}
        <div className="mt-6 rounded-2xl border border-border bg-card p-6 md:p-7" data-testid="results-exploration">
          <h2 className="font-heading font-extrabold tracking-tight text-xl">App exploration depth</h2>
          <p className="text-sm text-muted-foreground mt-1">Of the {data.exploration.tried} who tried the demo, how deeply they actually engaged.</p>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              ["Engaged", `${data.exploration.engaged} (${data.exploration.engaged_pct}%)`],
              ["Finished quiz", data.exploration.quiz_finished],
              ["Viewed roadmap", data.exploration.roadmap_viewed],
              ["Opened a lesson", data.exploration.lesson_openers],
              ["Avg lessons opened", data.exploration.avg_lessons_opened],
            ].map(([l, v]) => (
              <div key={l} className="rounded-xl bg-secondary p-4">
                <p className="font-heading font-extrabold text-2xl leading-none">{v}</p>
                <p className="text-xs text-muted-foreground mt-1.5">{l}</p>
              </div>
            ))}
          </div>
        </div>

        {/* answers */}
        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-border bg-card p-6 space-y-6">
            <Bars title="Want to learn AI now?" data={labelize("q1", data.answers.q1)} />
            <Bars title="Would you actually use this?" data={labelize("q6", data.answers.q6)} />
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 space-y-6">
            <Bars title="How they currently learn AI" data={labelize("q2", data.answers.q2)} />
            <Bars title="Ever paid for a course/app?" data={labelize("q4", data.answers.q4)} />
          </div>
        </div>

        {/* first reaction (MCQ) */}
        <div className="mt-6 rounded-2xl border border-border bg-card p-6" data-testid="results-reaction">
          <Bars title="Honest first reaction to the idea" data={labelize("q5", data.answers.q5 || {})} />
        </div>

        {/* frustration */}
        <div className="mt-6 rounded-2xl border border-border bg-card p-6" data-testid="results-frustration">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold text-base">"Scattered videos" frustration (1–5)</h3>
            <span className="text-sm text-muted-foreground">Avg <b className="text-foreground">{data.answers.q3.avg ?? "—"}</b> · {data.answers.q3.count} answers</span>
          </div>
          <div className="mt-4 flex items-end gap-3 h-28">
            {[1, 2, 3, 4, 5].map((n) => {
              const v = data.answers.q3.distribution[String(n)] || 0;
              const max = Math.max(1, ...Object.values(data.answers.q3.distribution));
              return (
                <div key={n} className="flex-1 flex flex-col items-center justify-end h-full">
                  <span className="text-xs text-muted-foreground mb-1">{v}</span>
                  <div className="w-full rounded-t-lg bg-primary" style={{ height: `${(v / max) * 100}%`, minHeight: v ? 6 : 2 }} />
                  <span className="text-xs font-semibold mt-1.5">{n}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* pricing */}
        <div className="mt-6">
          <h2 className="font-heading font-extrabold tracking-tight text-xl flex items-center gap-2"><IndianRupee className="h-5 w-5 text-primary" /> Pricing (per year)</h2>
          <p className="text-sm text-muted-foreground mt-1">A realistic fair-price range sits between "too cheap" and "too expensive".</p>
          <div className="mt-4 grid md:grid-cols-3 gap-4" data-testid="results-pricing">
            <PriceCard label="Too expensive to bother" stats={data.answers.pricing.too_expensive} />
            <PriceCard label="So cheap you'd doubt quality" stats={data.answers.pricing.too_cheap} />
            <PriceCard label="Genuinely fair price" stats={data.answers.pricing.fair} />
          </div>
        </div>

        {/* timeseries */}
        <div className="mt-6 rounded-2xl border border-border bg-card p-6 md:p-7" data-testid="results-timeseries">
          <h2 className="font-heading font-extrabold tracking-tight text-xl">Visits & responses by day</h2>
          {data.timeseries.length ? (
            <div className="mt-4 h-56">
              <ResponsiveContainer width="99%" height="100%" minHeight={224}>
                <BarChart data={data.timeseries} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 13 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="visits" name="Visits" fill="#7C3AED" radius={[5, 5, 0, 0]} maxBarSize={26} />
                  <Bar dataKey="responses" name="Responses" fill="#0055FF" radius={[5, 5, 0, 0]} maxBarSize={26} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground">No data yet — share your survey link to see the spike here.</p>
          )}
        </div>

        {/* open text + emails */}
        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-border bg-card p-6" data-testid="results-open-texts">
            <h3 className="font-heading font-bold text-base mb-3">Open feedback & comments ({data.open_texts.length})</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
              {data.open_texts.length ? data.open_texts.map((o, i) => (
                <div key={i} className="rounded-xl bg-secondary/60 p-3">
                  <p className="text-sm text-foreground whitespace-pre-wrap">{o.text}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {o.kind && <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-primary">{o.kind}</span>}
                    {o.q6 && <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Would use: {o.q6}</span>}
                  </div>
                </div>
              )) : <p className="text-sm text-muted-foreground">No written comments yet.</p>}
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6" data-testid="results-emails">
            <h3 className="font-heading font-bold text-base mb-3">Emails captured ({data.emails.length})</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {data.emails.length ? data.emails.map((e, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg bg-secondary/60 px-3 py-2">
                  <span className="text-sm font-medium truncate">{e.email}</span>
                  {e.q6 && <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground shrink-0 ml-2">{e.q6}</span>}
                </div>
              )) : <p className="text-sm text-muted-foreground">No emails yet.</p>}
            </div>
          </div>
        </div>
      </PageTransition>
    </div>
  );
}
